import { useRef, useState } from 'react';

import { Download, Pencil, Plus, Trash2, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreateScraper,
  useDeleteScraper,
  useImportScrapers,
  useScrapers,
  useTestScraper,
  useUpdateScraper,
} from '@/hooks/useScrapers';
import { scrapersApi } from '@/lib/api/scrapers.api';

import type { Scraper, ScraperConfig, ScraperRunResult } from '@repo/shared-types';

interface FormState {
  id?: string;
  name: string;
  description: string;
  config: string;
  is_active: boolean;
}

const STARTER_CONFIG = `{
  "request": {
    "base_url": "https://example.com/listing",
    "method": "GET",
    "fixed_params": {},
    "param_map": {}
  },
  "pagination": { "param": "page", "start": 1, "page_size": 12, "max_pages": 20 },
  "parse": {
    "record_selector": { "regex": "<li[^>]*>([\\\\s\\\\S]*?)</li>", "must_contain": "" },
    "fields": {
      "title": { "regex": "<h4[^>]*>([\\\\s\\\\S]*?)</h4>", "transform": "text" }
    },
    "dedupe_by": "title"
  },
  "client_filters": [],
  "max_results": 60
}`;

const EMPTY: FormState = {
  name: '',
  description: '',
  config: STARTER_CONFIG,
  is_active: true,
};

const textarea =
  'border-input bg-background ring-offset-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:outline-none';

function parseJson(value: string): unknown {
  if (!value.trim()) throw new Error('Config is required');
  try {
    return JSON.parse(value);
  } catch {
    throw new Error('Invalid JSON in config');
  }
}

export const ScrapersPage = () => {
  const { data: scrapers = [], isLoading } = useScrapers();
  const createMut = useCreateScraper();
  const updateMut = useUpdateScraper();
  const deleteMut = useDeleteScraper();
  const testMut = useTestScraper();
  const importMut = useImportScrapers();
  const fileRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<ScraperRunResult | null>(null);
  const [testArgs, setTestArgs] = useState('{}');

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const openNew = () => {
    setForm(EMPTY);
    setError('');
    setTestResult(null);
    setTestArgs('{}');
    setOpen(true);
  };

  const openEdit = (scraper: Scraper) => {
    setForm({
      id: scraper.id,
      name: scraper.name,
      description: scraper.description,
      config: JSON.stringify(scraper.config, null, 2),
      is_active: scraper.is_active,
    });
    setError('');
    setTestResult(null);
    setTestArgs('{}');
    setOpen(true);
  };

  const handleSave = async () => {
    setError('');
    try {
      const config = parseJson(form.config) as ScraperConfig;
      const payload = {
        name: form.name,
        description: form.description,
        config,
        is_active: form.is_active,
      };
      if (form.id) await updateMut.mutateAsync({ id: form.id, data: payload });
      else await createMut.mutateAsync(payload as never);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    }
  };

  const handleTest = async () => {
    setError('');
    setTestResult(null);
    try {
      const config = parseJson(form.config) as ScraperConfig;
      const args = JSON.parse(testArgs || '{}') as Record<string, unknown>;
      const result = await testMut.mutateAsync({ config, args });
      setTestResult(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed');
    }
  };

  const handleExport = async () => {
    const data = await scrapersApi.export();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'scrapers-export.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const json = JSON.parse(ev.target?.result as string);
        const list = Array.isArray(json) ? json : json.scrapers;
        await importMut.mutateAsync({ scrapers: list });
      } catch {
        setError('Invalid import file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Scrapers</h1>
          <p className="text-muted-foreground text-sm">
            Reusable, declarative HTML→JSON configs. Assign one to a custom tool (via its Scraper
            field) and the tool fetches + parses the site instead of calling an API.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={handleImportFile}
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" /> Import
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
          <Button onClick={openNew}>
            <Plus className="mr-2 h-4 w-4" /> New Scraper
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Base URL</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4}>Loading…</TableCell>
                </TableRow>
              )}
              {!isLoading && scrapers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">
                    No scrapers yet. Create one or import a set.
                  </TableCell>
                </TableRow>
              )}
              {scrapers.map((scraper) => (
                <TableRow key={scraper.id}>
                  <TableCell>
                    <div className="font-medium">{scraper.name}</div>
                    <div className="text-muted-foreground max-w-md truncate text-xs">
                      {scraper.description}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-xs truncate text-xs">
                    {scraper.config?.request?.base_url}
                  </TableCell>
                  <TableCell>
                    {scraper.is_active ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(scraper)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (
                          confirm(
                            `Delete scraper "${scraper.name}"? Tools using it will be detached.`,
                          )
                        )
                          deleteMut.mutate(scraper.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit scraper' : 'New scraper'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Name (snake_case)</label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.is_active} onCheckedChange={(v) => set('is_active', v)} />
                  Active
                </label>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea
                className={textarea}
                rows={2}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Config (ScraperConfig JSON)</label>
              <textarea
                className={textarea}
                rows={18}
                value={form.config}
                onChange={(e) => set('config', e.target.value)}
              />
            </div>

            {/* Test */}
            <div className="bg-muted/40 space-y-2 rounded-md border p-3">
              <label className="text-sm font-medium">Test args (JSON)</label>
              <textarea
                className={textarea}
                rows={3}
                value={testArgs}
                onChange={(e) => setTestArgs(e.target.value)}
              />
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testMut.isPending}>
                {testMut.isPending ? 'Running…' : 'Run test'}
              </Button>
              {testResult && (
                <div className="space-y-1">
                  <div className="text-muted-foreground text-xs">
                    {testResult.success
                      ? `${testResult.record_count ?? 0} record(s), ${testResult.pages_fetched ?? 0} page(s)${testResult.truncated ? ' (truncated)' : ''}`
                      : `Error: ${testResult.error}`}
                  </div>
                  <pre className="bg-background max-h-64 overflow-auto rounded border p-2 text-xs">
                    {JSON.stringify(testResult.records ?? testResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
